import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// The Age of MCP worker avatar — the player form, in the owner's colour, but
// visibly simpler: a shorter, stockier hexagonal body under a small head, topped
// with a work hat, plus a PROFESSION-SPECIFIC tool at its side so its job reads
// from its silhouette alone. Ported 1:1 from the game's `workerParts`; the
// geometry is preserved exactly.
//
// In the game the body colour is the owner's. Here the body parts are tagged
// `slot: "team"`, so `COLOR` is only the DEFAULT: a consumer recolours the
// worker to any team colour at build time via
// `instantiateModel(model, { slots: { team: "#e74c3c" } })` (the viewer's colour
// picker, the preview's `--team`). The hat, head, tools and bare shins keep
// their own colours, matching the game.
//
// (The bare rig actor that interaction animations pose is the separate
// `worker-simple` model; this is the richer in-game figure.)
//
// Each variant is one of the game's distinct profession silhouettes — every
// profession that shares a tool + hat reads identically, so it maps to one
// variant (noted per variant). The Baker/Miller wear a taller white cap; all
// others a flat brown work hat. The `seated` variant is the eating pose (origin
// at the seat contact, short bent legs hanging forward-down) the avatar layer
// swaps in at a bench. The carried-good overlay is omitted: it is parametric
// over every good id and depends on the game's `goods` module, which a model
// file may not import.
const COLOR = "#4f7a48";    // default team colour (the `team` slot's default; per-owner in game)
const HEAD = "#e7cda0";
const HANDLE = "#6b5740";   // wooden tool handle
const METAL = "#8a8580";    // tool head / blade
const SHIN = "#4a3c2c";     // bare shins on the seated pose

type Tool = "pick" | "axe" | "hammer" | "scythe" | "rod" | "crate" | "apron" | "spyglass" | "none";

// A profession's tool or accessory, carried at the worker's side, exactly as the
// game bakes it in `professionParts`.
function toolParts(tool: Tool): Part[] {
  switch (tool) {
    case "pick":
      return [
        { geometry: new THREE.CylinderGeometry(0.012, 0.012, 0.26, 5), color: HANDLE, position: [0.12, 0.16, 0.02], rotation: [0.2, 0, 0.25] },
        { geometry: new THREE.BoxGeometry(0.14, 0.022, 0.022), color: METAL, position: [0.115, 0.29, 0.05], rotation: [0, 0, 0.1] },
      ];
    case "axe":
      return [
        { geometry: new THREE.CylinderGeometry(0.012, 0.012, 0.24, 5), color: HANDLE, position: [0.12, 0.15, 0.02], rotation: [0, 0, 0.2] },
        { geometry: new THREE.BoxGeometry(0.055, 0.08, 0.016), color: METAL, position: [0.165, 0.25, 0.02] },
      ];
    case "hammer":
      return [
        { geometry: new THREE.CylinderGeometry(0.011, 0.011, 0.18, 5), color: HANDLE, position: [0.12, 0.13, 0.02], rotation: [0, 0, 0.15] },
        { geometry: new THREE.BoxGeometry(0.06, 0.05, 0.05), color: METAL, position: [0.145, 0.225, 0.02] },
      ];
    case "scythe":
      return [
        { geometry: new THREE.CylinderGeometry(0.012, 0.012, 0.3, 5), color: HANDLE, position: [0.12, 0.16, 0.02], rotation: [0.1, 0, 0.18] },
        { geometry: new THREE.BoxGeometry(0.13, 0.012, 0.02), color: METAL, position: [0.045, 0.3, 0.02], rotation: [0, 0, 0.55] },
      ];
    case "rod":
      return [
        { geometry: new THREE.CylinderGeometry(0.008, 0.004, 0.34, 5), color: "#8a6a3a", position: [0.12, 0.2, 0.06], rotation: [0.5, 0, 0.15] },
      ];
    case "crate":
      return [
        { geometry: new THREE.BoxGeometry(0.13, 0.11, 0.1), color: "#8a6a40", position: [0, 0.12, 0.12] },
        { geometry: new THREE.BoxGeometry(0.135, 0.02, 0.105), color: "#6b4f2c", position: [0, 0.17, 0.12] },
      ];
    case "apron":
      return [
        { geometry: new THREE.BoxGeometry(0.12, 0.1, 0.02), color: "#c8b48a", position: [0, 0.09, 0.075] },
      ];
    case "spyglass":
      return [
        { geometry: new THREE.CylinderGeometry(0.014, 0.018, 0.1, 6), color: "#3a3530", position: [0.07, 0.2, 0.07], rotation: [1.3, 0, 0] },
      ];
    default:
      return [];
  }
}

// The worker body: stocky hexagonal torso, small head, and a work hat (white and
// taller for a baker/miller, flat brown otherwise), with the profession tool at
// its side. `seated` swaps in the eating pose with bent legs hanging forward.
function worker_(tool: Tool, baker = false, seated = false): Part[] {
  const brimColor = baker ? "#e9e5da" : "#5a4632";
  const crownColor = baker ? "#f3f0e8" : "#6b5740";
  const crownH = baker ? 0.07 : 0.04;
  const legs: Part[] = seated
    ? [
        { geometry: new THREE.BoxGeometry(0.04, 0.038, 0.11), color: COLOR, position: [-0.032, 0.012, 0.06], slot: "team" },
        { geometry: new THREE.BoxGeometry(0.04, 0.038, 0.11), color: COLOR, position: [0.032, 0.012, 0.06], slot: "team" },
        { geometry: new THREE.BoxGeometry(0.038, 0.11, 0.038), color: SHIN, position: [-0.032, -0.05, 0.1] },
        { geometry: new THREE.BoxGeometry(0.038, 0.11, 0.038), color: SHIN, position: [0.032, -0.05, 0.1] },
      ]
    : [];
  return [
    ...legs,
    { geometry: new THREE.CylinderGeometry(0.058, 0.085, seated ? 0.13 : 0.15, 6),
      color: COLOR, position: [0, seated ? 0.065 : 0.075, 0], slot: "team" },
    { geometry: new THREE.IcosahedronGeometry(0.05, 0),
      color: HEAD, position: [0, seated ? 0.18 : 0.2, 0] },
    { geometry: new THREE.CylinderGeometry(0.079, 0.079, 0.012, 8),
      color: brimColor, position: [0, seated ? 0.213 : 0.233, 0] },
    { geometry: new THREE.CylinderGeometry(0.04, 0.05, crownH, 8),
      color: crownColor, position: [0, (seated ? 0.219 : 0.239) + crownH / 2, 0] },
    ...(seated ? [] : toolParts(tool)),
  ];
}

export const worker = defineModel({
  id: "worker",
  batchId: "characters",
  name: "Worker",
  tags: ["character", "avatar", "humanoid", "person", "worker"],
  summary: "The Age of MCP worker avatar — a stocky figure in a work hat with a profession tool.",
  description:
    "The in-game worker: the player form made simpler and stockier, in the owner's " +
    "colour, with a work hat and a profession-specific tool so its job reads from " +
    "its silhouette. Each variant is one of the game's distinct profession looks " +
    "(professions sharing a tool read alike). Ported from age-of-mcp's workerParts.",
  author: "age-of-mcp",
  footprint: 0.1,
  variants: [
    { id: "default", label: "Worker", description: "Idle worker, no held tool (also Baker/Miller share the body; Idle).", parts: () => worker_("none") },
    { id: "miner", label: "Miner", description: "Pick at the side. Professions: Miner, Quarrier.", parts: () => worker_("pick") },
    { id: "sawyer", label: "Sawyer", description: "Axe at the side. Professions: Sawyer, Refiner.", parts: () => worker_("axe") },
    { id: "mason", label: "Mason", description: "Hammer at the side. Professions: Mason, Smelter, Goldsmith, Jeweller, Operator.", parts: () => worker_("hammer") },
    { id: "farmer", label: "Farmer", description: "Scythe at the side. Profession: Farmer.", parts: () => worker_("scythe") },
    { id: "fisher", label: "Fisher", description: "Fishing rod at the side. Profession: Fisher.", parts: () => worker_("rod") },
    { id: "hauler", label: "Hauler", description: "Carry-crate at the chest. Professions: Hauler, Loader, Courier, Buyer.", parts: () => worker_("crate") },
    { id: "shopkeeper", label: "Shopkeeper", description: "Apron, no held tool. Profession: Shopkeeper.", parts: () => worker_("apron") },
    { id: "lookout", label: "Lookout", description: "Spyglass at the eye. Profession: Lookout.", parts: () => worker_("spyglass") },
    { id: "baker", label: "Baker", description: "Tall white cap, no held tool. Professions: Baker, Miller.", parts: () => worker_("none", true) },
    { id: "seated", label: "Seated", description: "The eating pose, settled with legs hanging forward.", parts: () => worker_("none", false, true) },
  ],
  // Where a carried good rides in front of the chest — the same anchor the game
  // uses for `heldGoodAt`. The good is supplied by the consumer at bake time
  // (`bakeSockets`), so the worker hauls any good without importing it.
  sockets: [
    { id: "carry", position: [0, 0.1, 0.12], slotType: "carry", description: "A good carried in front of the chest." },
  ],
  animations: [],
});
